import { User } from '@mi-front/core';
import Box from '@mui/material/Box';
import { useQuery } from '@tanstack/react-query';
import { QueryView } from '@ui/utils/async-view/QueryView';
import { getAppUserDisplayName } from '../../helpers/user';
import { fetchUser } from '../../modules/admin/api';
import { UserIcon } from './UserIcon';

interface UserDisplayProps {
  id?: string;
  user?: Pick<User, 'name' | 'surname' | 'email'>;
  showIcon?: boolean;
}

/** Wraps content with optional small user icon when showIcon is true. */
function wrapWithIcon(content: React.ReactNode, showIcon?: boolean) {
  if (showIcon) {
    return (
      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <UserIcon sx={{ fontSize: 16, mt: '-1px', color: 'text.secondary' }} />
        {content}
      </Box>
    );
  }
  return <>{content}</>;
}

export function UserLabel({ id, user, showIcon }: UserDisplayProps) {
  const userQuery = useQuery({
    queryKey: ['user', id],
    queryFn: () => fetchUser(id!),
    staleTime: Infinity,
    enabled: !!id,
  });

  // If user object is provided, use it directly
  if (user) {
    return wrapWithIcon(getAppUserDisplayName(user, 'long'), showIcon);
  }

  // If only ID is provided, fetch user data
  if (id) {
    return (
      <QueryView inline query={userQuery}>
        {(data) => wrapWithIcon(getAppUserDisplayName(data, 'long'), showIcon)}
      </QueryView>
    );
  }

  return null;
}
